import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { taskAutomationAPI } from '../../utils/crmApi';
import {
    ClipboardDocumentListIcon,
    PlusIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    PlayIcon,
    PauseIcon,
    StopIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    UserIcon,
    CalendarIcon,
    BoltIcon,
    CpuChipIcon,
    SparklesIcon,
    ArrowPathIcon,
    ChartBarIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useBranding } from '../../contexts/BrandingContext';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge,
    TableAvatar
} from '../../components/kibo-ui/table';

const TaskAutomation = () => {
    const navigate = useNavigate();
    const { branding } = useBranding();
    const { openDialog } = useDialog();
    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20 });
    const [selectedAutomations, setSelectedAutomations] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState('automations');

    // Automation status colors
    const getStatusColor = (status) => {
        const colors = {
            'active': 'bg-green-100 text-green-700',
            'paused': 'bg-yellow-100 text-yellow-700',
            'stopped': 'bg-red-100 text-red-700',
            'draft': 'bg-muted text-foreground',
            'error': 'bg-red-100 text-red-700',
            'running': 'bg-blue-100 text-blue-700'
        };
        return colors[status] || 'bg-muted text-foreground';
    };

    // Automation type colors
    const getTypeColor = (type) => {
        const colors = {
            'scheduled': 'bg-blue-100 text-blue-700',
            'triggered': 'bg-purple-100 text-purple-700',
            'ai_powered': 'bg-pink-100 text-pink-700',
            'workflow': 'bg-green-100 text-green-700',
            'integration': 'bg-orange-100 text-orange-700',
            'custom': 'bg-muted text-foreground'
        };
        return colors[type] || 'bg-muted text-foreground';
    };

    // Automation type icons
    const getTypeIcon = (type) => {
        const icons = {
            'scheduled': '⏰',
            'triggered': '⚡',
            'ai_powered': '🤖',
            'workflow': '🔄',
            'integration': '🔗',
            'custom': '⚙️'
        };
        return icons[type] || '⚙️';
    };

    useEffect(() => {
        loadAutomations();
        loadStats();
    }, [search, statusFilter, typeFilter, pagination.page]);

    const loadAutomations = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(search && { search }),
                ...(statusFilter && { status: statusFilter }),
                ...(typeFilter && { type: typeFilter })
            };

            const response = await taskAutomationAPI.getAll(params);
            if (response.data.success) {
                setAutomations(response.data.data);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error('Failed to load automations:', error);
            toast.error('Failed to load automations');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await taskAutomationAPI.getStats();
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load automation stats:', error);
        }
    };

    const handleSelectAutomation = (automationId) => {
        setSelectedAutomations(prev => 
            prev.includes(automationId) 
                ? prev.filter(id => id !== automationId)
                : [...prev, automationId]
        );
    };

    const handleSelectAll = () => {
        if (selectedAutomations.length === automations.length) {
            setSelectedAutomations([]);
        } else {
            setSelectedAutomations(automations.map(automation => automation._id));
        }
    };

    const handleBulkAction = (action) => {
        if (selectedAutomations.length === 0) return;
        
        const actionMessages = {
            'activate': 'activate',
            'pause': 'pause',
            'stop': 'stop',
            'delete': 'delete'
        };

        openDialog({
            title: `${actionMessages[action]} Automations`,
            description: `Are you sure you want to ${actionMessages[action]} ${selectedAutomations.length} automation(s)?`,
            confirmText: actionMessages[action].charAt(0).toUpperCase() + actionMessages[action].slice(1),
            cancelText: 'Cancel',
            variant: action === 'delete' ? 'danger' : 'warning',
            onConfirm: async () => {
                try {
                    setBulkActionLoading(true);
                    await taskAutomationAPI.bulkAction(selectedAutomations, action);
                    toast.success(`${selectedAutomations.length} automation(s) ${actionMessages[action]}ed successfully`);
                    setSelectedAutomations([]);
                    loadAutomations();
                } catch (error) {
                    console.error('Error performing bulk action:', error);
                    toast.error('Failed to perform bulk action');
                } finally {
                    setBulkActionLoading(false);
                }
            }
        });
    };

    const handleAutomationAction = async (automationId, action) => {
        try {
            await taskAutomationAPI.updateStatus(automationId, action);
            toast.success(`Automation ${action}ed successfully!`);
            loadAutomations();
        } catch (error) {
            console.error(`Error ${action}ing automation:`, error);
            toast.error(`Failed to ${action} automation`);
        }
    };

    const handleDeleteAutomation = (automationId) => {
        openDialog({
            title: 'Delete Automation',
            description: 'Are you sure you want to delete this automation? This action cannot be undone.',
            confirmText: 'Delete Automation',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await taskAutomationAPI.delete(automationId);
                    toast.success('Automation deleted successfully');
                    loadAutomations();
                } catch (error) {
                    console.error('Error deleting automation:', error);
                    toast.error('Failed to delete automation');
                }
            }
        });
    };

    const handleTestAutomation = async (automationId) => {
        try {
            await taskAutomationAPI.test(automationId);
            toast.success('Automation test started successfully!');
        } catch (error) {
            console.error('Error testing automation:', error);
            toast.error('Failed to test automation');
        }
    };

    const handleViewLogs = async (automationId) => {
        try {
            const response = await taskAutomationAPI.getLogs(automationId);
            // Open logs in a modal or new page
            console.log('Automation logs:', response.data);
        } catch (error) {
            console.error('Error fetching logs:', error);
            toast.error('Failed to fetch logs');
        }
    };

    const columns = [
        { accessor: 'select', label: '', sortable: false, width: 'w-12' },
        { accessor: 'name', label: 'Automation', sortable: true },
        { accessor: 'type', label: 'Type', sortable: true },
        { accessor: 'status', label: 'Status', sortable: true },
        { accessor: 'trigger', label: 'Trigger', sortable: true },
        { accessor: 'lastRun', label: 'Last Run', sortable: true },
        { accessor: 'successRate', label: 'Success Rate', sortable: true },
        { accessor: 'actions', label: 'Actions', sortable: false }
    ];

    const statusOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'active', label: 'Active' },
        { value: 'paused', label: 'Paused' },
        { value: 'stopped', label: 'Stopped' },
        { value: 'draft', label: 'Draft' },
        { value: 'error', label: 'Error' },
        { value: 'running', label: 'Running' }
    ];

    const typeOptions = [
        { value: '', label: 'All Types' },
        { value: 'scheduled', label: 'Scheduled' },
        { value: 'triggered', label: 'Triggered' },
        { value: 'ai_powered', label: 'AI Powered' },
        { value: 'workflow', label: 'Workflow' },
        { value: 'integration', label: 'Integration' },
        { value: 'custom', label: 'Custom' }
    ];

    const getActionButton = (automation) => {
        switch (automation.status) {
            case 'active':
                return (
                    <button
                        onClick={() => handleAutomationAction(automation._id, 'pause')}
                        className="text-yellow-600 hover:text-yellow-800 text-sm font-medium"
                        title="Pause Automation"
                    >
                        <PauseIcon className="h-4 w-4" />
                    </button>
                );
            case 'paused':
                return (
                    <button
                        onClick={() => handleAutomationAction(automation._id, 'activate')}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                        title="Activate Automation"
                    >
                        <PlayIcon className="h-4 w-4" />
                    </button>
                );
            case 'stopped':
            case 'draft':
                return (
                    <button
                        onClick={() => handleAutomationAction(automation._id, 'activate')}
                        className="text-green-600 hover:text-green-800 text-sm font-medium"
                        title="Start Automation"
                    >
                        <PlayIcon className="h-4 w-4" />
                    </button>
                );
            default:
                return null;
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatSuccessRate = (rate) => {
        return `${(rate * 100).toFixed(1)}%`;
    };

    const tabs = [
        { id: 'automations', name: 'Automations', icon: ClipboardDocumentListIcon },
        { id: 'templates', name: 'Templates', icon: BoltIcon },
        { id: 'logs', name: 'Execution Logs', icon: ChartBarIcon },
        { id: 'ai', name: 'AI Insights', icon: CpuChipIcon }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Task Automation</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Automate tasks, workflows, and processes with AI-powered intelligence
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 flex items-center gap-3">
                    {selectedAutomations.length > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleBulkAction('activate')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50"
                            >
                                <PlayIcon className="h-4 w-4 mr-1" />
                                Activate
                            </button>
                            <button
                                onClick={() => handleBulkAction('pause')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 disabled:opacity-50"
                            >
                                <PauseIcon className="h-4 w-4 mr-1" />
                                Pause
                            </button>
                            <button
                                onClick={() => handleBulkAction('stop')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                            >
                                <StopIcon className="h-4 w-4 mr-1" />
                                Stop
                            </button>
                            <button
                                onClick={() => handleBulkAction('delete')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                            >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Delete
                            </button>
                        </div>
                    )}
                        <Link
                            to="/task-automation/new"
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={{ backgroundColor: branding?.primaryColor, '--tw-ring-color': branding?.primaryColor }}
                        >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Create Automation
                    </Link>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-border">
                <nav className="-mb-px flex space-x-8">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`${
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-input'
                            } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                        >
                            <tab.icon className="h-4 w-4 mr-2" />
                            {tab.name}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Automation Stats */}
            {activeTab === 'automations' && stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Total Automations</p>
                                <p className="text-2xl font-semibold text-foreground">{stats.totalAutomations}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <PlayIcon className="h-8 w-8 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Active</p>
                                <p className="text-2xl font-semibold text-foreground">{stats.activeAutomations}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <CheckCircleIcon className="h-8 w-8 text-purple-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Tasks Executed</p>
                                <p className="text-2xl font-semibold text-foreground">{stats.tasksExecuted}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <ChartBarIcon className="h-8 w-8 text-orange-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                                <p className="text-2xl font-semibold text-foreground">{formatSuccessRate(stats.successRate)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full border border-input bg-background text-foreground rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Type</label>
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="w-full border border-input bg-background text-foreground rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                        >
                            {typeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setStatusFilter('');
                                setTypeFilter('');
                                setSearch('');
                            }}
                            className="w-full px-4 py-2 text-sm font-medium text-foreground bg-muted border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded-lg hover:bg-muted"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Enhanced Table with Kibo UI */}
            <TableProvider 
                data={automations} 
                columns={columns}
                onSort={(key, direction) => console.log('🔄 Table sort:', { key, direction })}
                onFilter={(key, value) => console.log('🔍 Table filter:', { key, value })}
            >
                <Table>
                    <TableHeader>
                        <TableSearch placeholder="Search automations by name, type, or description..." />
                    </TableHeader>
                    
                    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <TableHead>
                                <tr>
                                    {columns.map((column) => (
                                        <TableHeaderCell key={column.accessor} column={column}>
                                            {column.accessor === 'select' ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedAutomations.length === automations.length && automations.length > 0}
                                                    onChange={handleSelectAll}
                                                    className="h-4 w-4 text-blue-600 focus:ring-ring border-input rounded"
                                                />
                                            ) : (
                                                column.label
                                            )}
                                        </TableHeaderCell>
                                    ))}
                                </tr>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : automations.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="px-6 py-12 text-center">
                                            <div className="text-center">
                                                <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                                                <h3 className="mt-2 text-sm font-medium text-foreground">No automations found</h3>
                                                <p className="mt-1 text-sm text-muted-foreground">
                                                    Get started by creating your first automation.
                                                </p>
                                    <Link
                                        to="/task-automation/new"
                                        className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90"
                                        style={{ backgroundColor: branding?.primaryColor }}
                                    >
                                                    <PlusIcon className="h-5 w-5 mr-2" />
                                                    Create Automation
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    automations.map((automation) => (
                                        <TableRow 
                                            key={automation._id}
                                            onClick={() => navigate(`/task-automation/${automation._id}`)}
                                            className="cursor-pointer"
                                        >
                                            <TableCell className="w-12" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedAutomations.includes(automation._id)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectAutomation(automation._id);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="h-4 w-4 text-blue-600 focus:ring-ring border-input rounded"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center">
                                                    <div className="text-2xl mr-3">{getTypeIcon(automation.type)}</div>
                                                    <div>
                                                        <span
                                                            className="text-sm font-medium text-foreground"
                                                        >
                                                            {automation.name}
                                                        </span>
                                                        <div className="text-xs text-muted-foreground">
                                                            {automation.description}
                                                        </div>
                                                        {automation.isAI && (
                                                            <div className="flex items-center mt-1">
                                                                <SparklesIcon className="h-3 w-3 text-pink-500 mr-1" />
                                                                <span className="text-xs text-pink-600">AI Powered</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge 
                                                    status={automation.type}
                                                    className={getTypeColor(automation.type)}
                                                >
                                                    {automation.type.replace('_', ' ')}
                                                </StatusBadge>
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge 
                                                    status={automation.status}
                                                    className={getStatusColor(automation.status)}
                                                >
                                                    {automation.status.charAt(0).toUpperCase() + automation.status.slice(1)}
                                                </StatusBadge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    {automation.triggerType}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {automation.triggerDescription}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    {automation.lastRun ? formatDate(automation.lastRun) : 'Never'}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {automation.nextRun ? `Next: ${formatDate(automation.nextRun)}` : 'No schedule'}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm text-foreground">
                                                    {formatSuccessRate(automation.successRate)}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {automation.totalRuns} runs
                                                </div>
                                            </TableCell>
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center gap-2">
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        {getActionButton(automation)}
                                                    </div>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleTestAutomation(automation._id);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                        title="Test Automation"
                                                    >
                                                        <ArrowPathIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewLogs(automation._id);
                                                        }}
                                                        className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                        title="View Logs"
                                                    >
                                                        <ChartBarIcon className="h-4 w-4" />
                                                    </button>
                                                    <Link
                                                        to={`/task-automation/${automation._id}`}
                                                        className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                        title="View"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <EyeIcon className="h-4 w-4" />
                                                    </Link>
                                                    <Link
                                                        to={`/task-automation/${automation._id}/edit`}
                                                        className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                        title="Edit"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                    </Link>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteAutomation(automation._id);
                                                        }}
                                                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                                                        title="Delete"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </table>
                    </div>
                    
                    <TableFooter>
                        <div className="px-6 py-3 border-t border-border">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-foreground">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className="px-3 py-1 text-sm text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1 text-sm text-foreground">
                                        Page {pagination.page} of {pagination.pages}
                                    </span>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page === pagination.pages}
                                        className="px-3 py-1 text-sm text-foreground bg-card border border-input focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground rounded hover:bg-muted disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </TableFooter>
                </Table>
            </TableProvider>

            {/* Other tabs content would go here */}
            {activeTab !== 'automations' && (
                <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                    <div className="text-center">
                        <div className="mx-auto h-12 w-12 text-muted-foreground">
                            {(() => {
                                const activeTabData = tabs.find(tab => tab.id === activeTab);
                                const IconComponent = activeTabData?.icon;
                                return IconComponent ? <IconComponent className="h-12 w-12" /> : null;
                            })()}
                        </div>
                        <h3 className="mt-2 text-sm font-medium text-foreground">
                            {tabs.find(tab => tab.id === activeTab)?.name}
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            This section will contain {activeTab} functionality.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskAutomation;
